import { DYNAMIC_COLORS, DYNAMIC_LEVELS } from '../utils/dynamicColors.js';

const join = (...classes) => classes.filter(Boolean).join(' ');

const DISABLED_CLASS = 'opacity-50 cursor-not-allowed';

export const adjacentLevels = (level) => {
	const currentLevel = level?.toString();
	const index = DYNAMIC_LEVELS.indexOf(currentLevel);

	if (index < 0) {
		return [currentLevel, currentLevel];
	}

	return [
		DYNAMIC_LEVELS[Math.min(index + 1, DYNAMIC_LEVELS.length - 1)],
		DYNAMIC_LEVELS[Math.max(index - 1, 0)],
	];
};

const buildColorClass = ({ bgColor, textColor, hoverColor = '', activeColor, borderColor = '' }, isDisabled) =>
	join(bgColor, borderColor, textColor, isDisabled ? DISABLED_CLASS : `${hoverColor} ${activeColor}`);

export const COLOR_VARIANTS = {
	outline: ({ color, level, active }, isDisabled) =>
		buildColorClass({
			bgColor: active ? `bg-${color}-50 dark:bg-${color}-700` : 'bg-transparent',
			textColor: `text-${color}-${level} hover:text-white`,
			hoverColor: active ? '' : `hover:bg-${color}-${level} dark:hover:bg-${color}-${level}`,
			activeColor: `active:bg-${color}-${level} dark:active:bg-${color}-${level}`,
			borderColor: `border border-${color}-${level}`,
		}, isDisabled),

	solid: ({ color, level, active, increaseLevel, decreaseLevel }, isDisabled) =>
		buildColorClass({
			bgColor: active ? `bg-${color}-${decreaseLevel}` : `bg-${color}-${level}`,
			textColor: 'text-white',
			hoverColor: active ? '' : `hover:bg-${color}-${increaseLevel}`,
			activeColor: `active:bg-${color}-${decreaseLevel}`,
		}, isDisabled),

	twoTone: ({ color, level, active }, isDisabled) =>
		buildColorClass({
			bgColor: active
				? `bg-${color}-200 dark:bg-${color}-50`
				: `bg-${color}-50 dark:bg-${color}-500 dark:bg-opacity-20`,
			textColor: `text-${color}-${level} dark:text-${color}-50`,
			hoverColor: active ? '' : `hover:bg-${color}-100 dark:hover:bg-${color}-500 dark:hover:bg-opacity-30`,
			activeColor: `active:bg-${color}-200 dark:active:bg-${color}-500 dark:active:bg-opacity-40`,
		}, isDisabled),

	ghost: ({ color, level, active }, isDisabled) =>
		buildColorClass({
			bgColor: active ? 'bg-neutral-100 dark:bg-neutral-700/60' : 'bg-transparent',
			textColor: active
				? `text-${color}-${level}`
				: `text-neutral-500 dark:text-neutral-400 hover:text-${color}-${level}`,
			hoverColor: active ? '' : 'hover:bg-neutral-100 dark:hover:bg-neutral-700/60',
			activeColor: 'active:bg-neutral-200 dark:active:bg-neutral-700',
			borderColor: 'border border-transparent',
		}, isDisabled),

	plain: ({ active }, isDisabled) =>
		buildColorClass({
			bgColor: active ? 'bg-neutral-100 dark:bg-neutral-500' : 'bg-transparent border border-transparent',
			textColor: 'text-neutral-600 dark:text-neutral-100',
			hoverColor: active ? '' : 'hover:bg-neutral-50 dark:hover:bg-neutral-600',
			activeColor: 'active:bg-neutral-100 dark:active:bg-neutral-500 dark:active:border-neutral-500',
		}, isDisabled),

	default: ({ active }, isDisabled) =>
		buildColorClass({
			bgColor: active
				? 'bg-neutral-100 border border-neutral-300 dark:bg-neutral-500 dark:border-neutral-500'
				: 'bg-white border border-neutral-300 dark:bg-neutral-700 dark:border-neutral-700',
			textColor: 'text-neutral-600 dark:text-neutral-100',
			hoverColor: active ? '' : 'hover:bg-neutral-50 dark:hover:bg-neutral-600',
			activeColor: 'active:bg-neutral-100 dark:active:bg-neutral-500 dark:active:border-neutral-500',
		}, isDisabled),
};

export const buttonColorClasses = (color, level) => {
	const [increaseLevel, decreaseLevel] = adjacentLevels(level);

	return Object.values(COLOR_VARIANTS).flatMap((variant) => [false, true].flatMap((active) =>
		variant({ color, level, active, increaseLevel, decreaseLevel }, false).split(/\s+/).filter(Boolean),
	));
};
